import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { AlertIcon, Text, useTooltip, useModal, Image, HelpIcon } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import NftStakeModal, { NFT_STAKE } from 'views/Farms/components/NftStakeModal'
import NftViewStakeModal from 'views/Farms/components/NftViewStakeModal'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import useGetNftStakeInfo from 'views/Farms/hooks/useGetNftStakeInfo'
import { NFT_TYPE } from 'config/constants/nftTypes'
import { isEcoFarmSupported } from 'utils/isNftStakingSupported'
import { ActionButtons, BoostContent } from './styles'

const StyledText = styled(Text)<{ disabled }>`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`

interface NFTBoostActionProps {
  disabled: boolean
  farmId: number
  iconWidth?: number
  icon?: string
  name?: string
}

const NFTBoostAction: React.FC<NFTBoostActionProps> = ({
  disabled = true,
  farmId,
  iconWidth = 30,
  icon = '/images/xVVS/boost-farm-NFT.png',
  name,
}) => {
  const { t } = useTranslation()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(t('Stake your NFT to enjoy additional boost rewards'), {
    placement: 'top',
  })
  const {
    targetRef: warningRef,
    tooltip: warningTooltip,
    tooltipVisible: warningTooltipVisible,
  } = useTooltip(t('You need to start xVVS vault boost first before boosting by NFT'), {
    placement: 'top',
  })

  // Miner Mole & ECO farm
  const {
    isApprovedForAll,
    nftBalanceOfUser,
    boostingNfts,
    isLoading: isGetNftStakeInfoLoading,
  } = useGetNftStakeInfo(farmId)

  const initStepForNftStake = useMemo(() => {
    return {
      initStep: isGetNftStakeInfoLoading
        ? NFT_STAKE.loading
        : isApprovedForAll
        ? nftBalanceOfUser > 0
          ? NFT_STAKE.emptyStaked
          : NFT_STAKE.emptyNfts
        : NFT_STAKE.approve,
      isBtnDisabled: isGetNftStakeInfoLoading,
    }
  }, [isApprovedForAll, isGetNftStakeInfoLoading, nftBalanceOfUser])

  const [onPresentNftStakeModal] = useModal(
    <NftStakeModal
      initStep={initStepForNftStake?.initStep}
      farmId={farmId}
      type={isEcoFarmSupported(farmId) ? NFT_TYPE.ARGO : NFT_TYPE.MINER_MOLE}
    />,
  )

  const [onPresentNftViewStakeModal] = useModal(
    <NftViewStakeModal
      farmId={farmId}
      boostingNfts={boostingNfts as []}
      type={isEcoFarmSupported(farmId) ? NFT_TYPE.ARGO : NFT_TYPE.MINER_MOLE}
    />,
  )

  const clickButton = useCallback(async () => {
    if (initStepForNftStake?.isBtnDisabled || isNilOrUndef(farmId)) return
    if (boostingNfts?.length > 0) {
      onPresentNftViewStakeModal()
      return
    }
    onPresentNftStakeModal()
  }, [
    boostingNfts?.length,
    farmId,
    initStepForNftStake?.isBtnDisabled,
    onPresentNftStakeModal,
    onPresentNftViewStakeModal,
  ])

  return (
    <ActionButtons>
      <BoostContent>
        <Image width={iconWidth} height={24} src={icon} alt={name || 'xVVS'} />
        <Text fontSize="13px">
          {name
            ? t('%name% NFT BOOST', {
                name,
              })
            : t('NFT STAKE BOOST')}
        </Text>
        {disabled ? (
          <>
            <span ref={warningRef}>
              <AlertIcon />
            </span>
            {warningTooltipVisible && warningTooltip}
          </>
        ) : (
          <>
            <span ref={targetRef}>
              <HelpIcon width="16px" height="16px" color="primary" mt="4px" />
            </span>
            {tooltipVisible && tooltip}
          </>
        )}
      </BoostContent>
      <StyledText
        disabled={disabled || initStepForNftStake?.isBtnDisabled}
        fontSize="16px"
        color="primary"
        bold
        onClick={clickButton}
      >
        {(disabled ? '' : '+/-') + t('Boost')}
      </StyledText>
    </ActionButtons>
  )
}

export default NFTBoostAction
